import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Package, Plus, Trash, PencilSimple, X, Check } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { adminHeaders } from "@/lib/adminAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductManager = ({ products, meta, onChange }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const remove = async (id) => {
    if (!window.confirm("Delete this product? PDFs stay but become standalone.")) return;
    try {
      await axios.delete(`${API}/admin/products/${id}`, { headers: adminHeaders() });
      toast.success("Deleted");
      onChange();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Package size={22} weight="duotone" className="text-primary" />
          <h2 className="font-fredoka font-bold text-lg text-foreground">Product Bundles</h2>
          <span className="text-xs font-nunito text-muted-foreground">
            ({products.length}) — group PDFs into a workbook, guide + answer key set
          </span>
        </div>
        <Button
          onClick={() => {
            setEditingId(null);
            setShowForm(true);
          }}
          size="sm"
          className="bg-accent-purple hover:bg-purple-600 text-white font-nunito font-bold border-2 border-foreground shadow-brutal-sm rounded-full"
          data-testid="admin-add-product-btn"
        >
          <Plus size={16} weight="bold" className="mr-1" />
          New Product
        </Button>
      </div>

      {products.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-foreground/25 rounded-xl p-6 text-center">
          <p className="font-nunito text-sm text-muted-foreground">
            No product bundles yet. Click "New Product" to create your first one — you can then attach PDFs to it during upload.
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {products.map((p) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border-2 border-foreground rounded-xl shadow-brutal-sm p-4"
              data-testid={`admin-product-${p.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-fredoka font-bold text-foreground truncate">{p.title}</p>
                  {p.description && (
                    <p className="font-nunito text-xs text-muted-foreground mt-0.5 line-clamp-2">
                      {p.description}
                    </p>
                  )}
                  <div className="flex gap-1 mt-2 flex-wrap">
                    <span className="text-xs font-nunito font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                      {p.topic}
                    </span>
                    {p.grades?.length > 0 && (
                      <span className="text-xs font-nunito font-bold bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                        {p.grades.length === 1 ? `Grade ${p.grades[0]}` : `Grades ${p.grades.join(", ")}`}
                      </span>
                    )}
                    <span className="text-xs font-nunito font-bold bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                      {p.pdf_count || 0} PDF{p.pdf_count === 1 ? "" : "s"}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button
                    onClick={() => {
                      setEditingId(p.id);
                      setShowForm(true);
                    }}
                    className="p-1.5 rounded-lg hover:bg-muted"
                    data-testid={`admin-edit-product-${p.id}`}
                    aria-label="Edit product"
                  >
                    <PencilSimple size={16} />
                  </button>
                  <button
                    onClick={() => remove(p.id)}
                    className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"
                    data-testid={`admin-delete-product-${p.id}`}
                    aria-label="Delete product"
                  >
                    <Trash size={16} />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {showForm && (
          <ProductForm
            product={editingId ? products.find((p) => p.id === editingId) : null}
            meta={meta}
            onClose={() => {
              setShowForm(false);
              setEditingId(null);
            }}
            onSaved={() => {
              setShowForm(false);
              setEditingId(null);
              onChange();
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const ProductForm = ({ product, meta, onClose, onSaved }) => {
  const isEdit = !!product;
  const [form, setForm] = useState({
    title: product?.title || "",
    description: product?.description || "",
    topic: product?.topic || meta.topics?.[0] || "Other",
    grades: product?.grades || ["K"],
  });
  const [saving, setSaving] = useState(false);

  const toggleGrade = (g) => {
    setForm((prev) => ({
      ...prev,
      grades: prev.grades.includes(g)
        ? prev.grades.filter((x) => x !== g)
        : [...prev.grades, g],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Title required");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.patch(`${API}/admin/products/${product.id}`, form, {
          headers: adminHeaders(),
        });
      } else {
        await axios.post(`${API}/admin/products`, form, {
          headers: adminHeaders(),
        });
      }
      toast.success(isEdit ? "Updated" : "Created");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white border-2 border-foreground rounded-2xl shadow-brutal max-w-lg w-full p-6"
        data-testid="admin-product-form"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-fredoka font-bold text-xl text-foreground">
            {isEdit ? "Edit Product" : "New Product Bundle"}
          </h2>
          <button type="button" onClick={onClose} className="p-1 rounded-lg hover:bg-muted">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <Input
            placeholder="Product title (e.g. Weak to Strong Verbs Workbook)"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="font-nunito border-2 border-foreground rounded-xl"
            data-testid="admin-product-title"
          />
          <Textarea
            placeholder="Description (optional)"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="font-nunito border-2 border-foreground rounded-xl"
            rows={2}
          />
          <div>
            <label className="font-nunito text-sm font-bold text-foreground mb-1 block">Topic</label>
            <select
              value={form.topic}
              onChange={(e) => setForm({ ...form, topic: e.target.value })}
              className="w-full font-nunito border-2 border-foreground rounded-xl px-3 py-2 bg-white"
            >
              {(meta.topics || []).map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="font-nunito text-sm font-bold text-foreground mb-2 block">Grades</label>
            <div className="flex flex-wrap gap-2">
              {(meta.grades || []).map((g) => {
                const active = form.grades.includes(g);
                return (
                  <button
                    key={g}
                    type="button"
                    onClick={() => toggleGrade(g)}
                    className={`px-4 py-1.5 rounded-full border-2 border-foreground font-nunito font-bold text-sm ${
                      active ? "bg-primary text-white" : "bg-white text-foreground hover:bg-muted"
                    }`}
                  >
                    {g === "K" ? "K" : `Grade ${g}`}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 font-nunito border-2 border-foreground rounded-xl">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="flex-1 bg-primary hover:bg-primary-hover text-white font-nunito font-bold border-2 border-foreground shadow-brutal active-brutal rounded-xl"
              data-testid="admin-product-save"
            >
              <Check size={16} weight="bold" className="mr-1" />
              {saving ? "Saving…" : isEdit ? "Save" : "Create"}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default ProductManager;
